/**
 * LotAllocationMassUploadDialog — bulk insert nhiều dòng Lot Allocation cùng
 * lúc. Pattern tham khảo nút "Mass upload" ở `/futures/orders`
 * (FuturesOrdersListView): grid editable + paste TSV từ Excel + 1 POST bulk.
 *
 * Cột (theo field bảng `lot_allocation`): Lot ID, Stack ID, GRN ID (optional),
 * Bag Allocated, Kg Allocated.
 *
 * Combo (giống dialog đơn `LotAllocationFormDialog`): Lot ID / Stack ID / GRN ID
 * là Autocomplete RESTRICTED — chỉ chọn được giá trị trong RowSource, KHÔNG nhập
 * tự do giá trị ngoài list. GRN ID keyed theo Stack của chính dòng đó (Access
 * `stack_id_AfterUpdate`); đổi Stack → tự AUTO-PICK GRN đầu tiên của stack (chưa
 * chọn). Paste TSV cũng chỉ giữ giá trị khớp list, giá trị lạ bị bỏ.
 *
 * Bag Allocated / Kg Allocated: nhập tay nhưng CHỈ ký tự số (chặn chữ), blur →
 * format Standard `#,##0.00`; auto-fill từ Pending của (Stack→GRN) qua snapshot
 * (gõ đè được). Nhiều dòng cùng GRN → cross-check cộng dồn chặn vượt Pending.
 *
 * Cross-check (all-or-nothing): nút "Cross-check" + auto khi Upload gọi BE dry-run
 * `…/lot-allocation-mass/validate` — validate CỘNG DỒN pending pool theo (lot) +
 * (grn|stack) khi nhiều dòng cùng key chia chung quota; verdict per dòng →
 * highlight đỏ + ✓/✗ + tooltip "Row N: … exceeds … (remaining/requested)". Còn
 * dòng lỗi → Upload disabled. Upload sạch → BE re-validate (FOR UPDATE, race-safe)
 * + insert trong 1 transaction (lỗi 1 dòng → rollback hết).
 */
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useBulkCreateLotAllocationMutation,
  useGetLotAllocationDoOptionsQuery,
  useGetLotAllocationGrnOptionsQuery,
  useGetLotAllocationOptionsQuery,
  useGetLotAllocationSnapshotQuery,
  useValidateLotAllocationMassMutation,
  type FactoryCell,
} from '@/store/api/factoryApi'

/** Verdict 1 dòng sau cross-check (map theo index dòng gốc trong grid). */
interface RowVerdict {
  ok: boolean
  messages: string[]
  badFields: Set<string>
  /** Gợi ý sửa dòng lỗi (vd "Suggested max here: Bag ≤ …, Kg ≤ …"). */
  suggestion?: string
}
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

interface MassRow {
  lot_id: string
  stack_id: string
  grn_id: string
  do_no: string
  bag_allocated: string
  kg_allocated: string
}

type FieldKey = keyof MassRow
type FieldKind = 'lot' | 'stack' | 'grn' | 'do' | 'number'

const FIELDS: Array<{ key: FieldKey; label: string; kind: FieldKind; required: boolean }> = [
  { key: 'lot_id', label: 'Lot ID', kind: 'lot', required: true },
  { key: 'stack_id', label: 'Stack ID', kind: 'stack', required: true },
  { key: 'grn_id', label: 'GRN ID', kind: 'grn', required: false },
  { key: 'do_no', label: 'DO No', kind: 'do', required: false },
  { key: 'bag_allocated', label: 'Bag Allocated', kind: 'number', required: true },
  { key: 'kg_allocated', label: 'Kg Allocated', kind: 'number', required: true },
]

/** Số combo restricted đứng trước nhóm cột số (lot, stack, grn, do). Dùng để
 *  cắt FIELDS giữa combo vs number cell. */
const COMBO_COUNT = 4

const emptyRow = (): MassRow => ({
  lot_id: '',
  stack_id: '',
  grn_id: '',
  do_no: '',
  bag_allocated: '',
  kg_allocated: '',
})

/** Strip thousand-sep + space → numeric string ('' nếu không parse được). */
function normNumber(raw: string): string {
  const cleaned = raw.replace(/,/g, '').replace(/\s+/g, '').trim()
  if (cleaned === '') return ''
  const n = Number(cleaned)
  return Number.isFinite(n) ? String(n) : ''
}

/** Access Format Standard `#,##0.00` — '' cho null/undefined (clear cell). */
function formatStandard(v: number | null | undefined): string {
  if (v === null || v === undefined) return ''
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Khớp `raw` với một option (trim + case-insensitive) → trả về option chuẩn,
 *  '' nếu không khớp. Dùng để chặn giá trị paste ngoài RowSource. */
function canonicalOption(options: string[], raw: string): string {
  const v = (raw ?? '').replace(/\r$/, '').trim()
  if (!v) return ''
  const hit = options.find((o) => o.toLowerCase() === v.toLowerCase())
  return hit ?? ''
}

/** "a\tb\nc\td" → [['a','b'],['c','d']]. */
function parseTsv(text: string): string[][] {
  const trimmed = text.replace(/\r/g, '').replace(/\n$/, '')
  if (!trimmed) return []
  return trimmed.split('\n').map((line) => line.split('\t'))
}

function toNum(v: string): number | null {
  const s = v.replace(/,/g, '').trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

interface Props {
  open: boolean
  factoryCode: string
  onClose: () => void
  /** Gọi sau khi upload thành công (parent có thể refetch / đóng). */
  onUploaded?: () => void
}

export function LotAllocationMassUploadDialog({ open, factoryCode, onClose, onUploaded }: Props) {
  const snack = useSnackbar()
  const [rows, setRows] = useState<MassRow[]>([emptyRow()])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [verdictByRow, setVerdictByRow] = useState<Record<number, RowVerdict>>({})
  const [bulkCreate, { isLoading: uploading }] = useBulkCreateLotAllocationMutation()
  const [validateMass, { isLoading: checking }] = useValidateLotAllocationMassMutation()

  // Sửa bất kỳ ô nào → verdict cũ stale, xoá để bắt cross-check lại (all-or-nothing).
  const clearVerdicts = () => setVerdictByRow((v) => (Object.keys(v).length ? {} : v))
  const anyError = Object.values(verdictByRow).some((v) => !v.ok)

  // Combo RowSource (Access Form_Load): lot_id + stack_id. grn_id keyed theo
  // stack của từng dòng → fetch trong RowCells.
  const optionsQuery = useGetLotAllocationOptionsQuery({ factoryCode }, { skip: !open })
  const lotIdOptions = optionsQuery.data?.lot_ids ?? []
  const stackIdOptions = optionsQuery.data?.stack_ids ?? []

  // DO No combo (delivery_order_scs, shared) — fetch 1 lần, dùng chung mọi dòng.
  const doOptionsQuery = useGetLotAllocationDoOptionsQuery({ factoryCode }, { skip: !open })
  const doNoOptions = (doOptionsQuery.data ?? []).map((o) => o.do_no)

  const reset = () => {
    setRows([emptyRow()])
    setSelected(new Set())
    setVerdictByRow({})
  }

  const handleClose = () => {
    if (uploading) return
    onClose()
  }

  const setCell = (rowIdx: number, field: FieldKey, value: string) => {
    clearVerdicts()
    setRows((cur) => cur.map((r, i) => (i === rowIdx ? { ...r, [field]: value } : r)))
  }

  // stack_id đổi → clear grn_id (Access stack_id_AfterUpdate).
  const setStackCell = (rowIdx: number, value: string) => {
    clearVerdicts()
    setRows((cur) =>
      cur.map((r, i) =>
        i === rowIdx ? (r.stack_id === value ? r : { ...r, stack_id: value, grn_id: '' }) : r,
      ),
    )
  }

  // Paste TSV bắt đầu từ ô (rowIdx, colIdx) — mở rộng grid xuống/sang phải.
  // Combo: chỉ giữ giá trị khớp RowSource (lot/stack canonical ngay; grn để
  // RowCells validate khi đã có options theo stack).
  const handlePaste = (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData?.getData('text') ?? ''
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return
    e.preventDefault()
    e.stopPropagation()
    clearVerdicts()
    const matrix = parseTsv(text)
    const coerce = (kind: FieldKind, raw: string): string => {
      if (kind === 'lot') return canonicalOption(lotIdOptions, raw)
      if (kind === 'stack') return canonicalOption(stackIdOptions, raw)
      if (kind === 'do') return canonicalOption(doNoOptions, raw)
      if (kind === 'grn') return (raw ?? '').replace(/\r$/, '').trim()
      // Bag/Kg: parse rồi format Standard #,##0.00 (cell read-only, hiển thị chuẩn).
      const s = normNumber((raw ?? '').replace(/\r$/, ''))
      return s === '' ? '' : formatStandard(Number(s))
    }
    setRows((cur) => {
      const next = cur.map((r) => ({ ...r }))
      matrix.forEach((line, r) => {
        const targetRow = rowIdx + r
        while (next.length <= targetRow) next.push(emptyRow())
        line.forEach((cellRaw, c) => {
          const targetCol = colIdx + c
          const fld = FIELDS[targetCol]
          if (!fld) return
          next[targetRow][fld.key] = coerce(fld.kind, cellRaw)
        })
      })
      return next
    })
  }

  const toggleRow = (idx: number) =>
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected((cur) => (cur.size === rows.length ? new Set() : new Set(rows.map((_, i) => i))))

  const deleteSelected = () => {
    if (selected.size === 0) return
    clearVerdicts()
    setRows((cur) => {
      const kept = cur.filter((_, i) => !selected.has(i))
      return kept.length ? kept : [emptyRow()]
    })
    setSelected(new Set())
  }

  const errDetail = (err: unknown): string | undefined =>
    typeof err === 'object' && err && 'data' in err
      ? (err as { data?: { detail?: string } }).data?.detail
      : undefined

  // Dòng đã nhập (kèm index gốc trong grid) — bỏ dòng trống hoàn toàn.
  const getFilled = () =>
    rows.map((r, i) => ({ r, i })).filter(({ r }) => FIELDS.some((f) => r[f.key].trim() !== ''))

  const buildPayload = (filled: { r: MassRow }[]): Record<string, FactoryCell | undefined>[] =>
    filled.map(({ r }) => ({
      lot_id: r.lot_id.trim(),
      stack_id: r.stack_id.trim(),
      grn_id: r.grn_id.trim() || undefined,
      do_no: r.do_no.trim() || undefined,
      bag_allocated: toNum(r.bag_allocated),
      kg_allocated: toNum(r.kg_allocated),
    }))

  // Cross-check dry-run: BE validate cộng dồn pending → map verdict theo index
  // dòng gốc để highlight. Trả true nếu tất cả dòng OK.
  const runValidate = async (opts?: { quiet?: boolean }): Promise<boolean> => {
    const filled = getFilled()
    if (filled.length === 0) {
      snack.warning('At least 1 row is required')
      return false
    }
    try {
      const res = await validateMass({ factoryCode, rows: buildPayload(filled) }).unwrap()
      const map: Record<number, RowVerdict> = {}
      res.rows.forEach((v, k) => {
        const origIdx = filled[k]?.i
        if (origIdx == null) return
        map[origIdx] = {
          ok: v.ok,
          messages: v.errors.map((e) => e.message),
          badFields: new Set(v.errors.map((e) => e.field).filter((f): f is NonNullable<typeof f> => !!f)),
          suggestion: v.suggestion?.message,
        }
      })
      setVerdictByRow(map)
      const badCount = res.rows.filter((v) => !v.ok).length
      if (badCount > 0) snack.error(`Cross-check: ${badCount} row(s) failed — fix highlighted rows`)
      else if (!opts?.quiet) snack.success(`Cross-check passed (${res.rows.length} rows)`)
      return badCount === 0
    } catch (err) {
      snack.error(errDetail(err) || 'Could not run cross-check')
      return false
    }
  }

  // Upload = auto cross-check trước (all-or-nothing). Sạch mới insert.
  const runUpload = async () => {
    const ok = await runValidate({ quiet: true })
    if (!ok) return
    const filled = getFilled()
    try {
      const res = await bulkCreate({ factoryCode, rows: buildPayload(filled) }).unwrap()
      snack.success(`Uploaded ${res.created}/${filled.length} rows`)
      reset()
      onUploaded?.()
      onClose()
    } catch (err) {
      // Lỗi lúc insert (vd race) → surface + cross-check lại để chỉ đúng dòng.
      snack.error(errDetail(err) || 'Could not upload rows')
      void runValidate({ quiet: true })
    }
  }

  const allSelected = rows.length > 0 && selected.size === rows.length

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (uploading || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: 'min(720px, 92vh)' } }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          component="span"
          noWrap
          title="Mass upload — Lot Allocation"
          sx={{ ...TEXT_TRUNCATE, flex: 1, fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {'Mass upload — '}
          </Box>
          Lot Allocation
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary">
          {factoryCode.toUpperCase()}
        </Typography>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={{ pt: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Paste from Excel (columns: Lot ID, Stack ID, GRN ID, DO No, Bag Allocated, Kg Allocated).
          Lot ID, Stack ID, GRN ID and DO No must match an existing value — anything else is dropped.
          GRN ID and DO No are optional. Empty rows are skipped on upload.
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" onClick={() => { clearVerdicts(); setRows((c) => [...c, emptyRow()]) }}>
            Add row
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selected.size === 0}
            onClick={deleteSelected}
          >
            Delete selected
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={reset}>
            Reset
          </Button>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `40px 40px 36px repeat(${FIELDS.length}, minmax(120px, 1fr))`,
            border: (t) => `1px solid ${t.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            '& > .hdr': {
              bgcolor: 'action.hover',
              fontWeight: 700,
              fontSize: '0.72rem',
              px: 1,
              py: 0.75,
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
            },
          }}
        >
          {/* Header row */}
          <Box className="hdr" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox size="small" checked={allSelected} onChange={toggleAll} sx={{ p: 0 }} />
          </Box>
          <Box className="hdr" sx={{ textAlign: 'center' }}>#</Box>
          <Box className="hdr" sx={{ textAlign: 'center' }} title="Cross-check status" aria-label="Cross-check status" />
          {FIELDS.map((f) => (
            <Box key={f.key} className="hdr">
              {f.label}
              {f.required ? <span style={{ color: '#d32f2f' }}> *</span> : null}
            </Box>
          ))}

          {/* Data rows */}
          {rows.map((row, rowIdx) => (
            <RowCells
              key={rowIdx}
              row={row}
              rowIdx={rowIdx}
              factoryCode={factoryCode}
              lotIdOptions={lotIdOptions}
              stackIdOptions={stackIdOptions}
              doNoOptions={doNoOptions}
              selected={selected.has(rowIdx)}
              verdict={verdictByRow[rowIdx]}
              onToggle={() => toggleRow(rowIdx)}
              onCell={(field, value) => setCell(rowIdx, field, value)}
              onStack={(value) => setStackCell(rowIdx, value)}
              onPaste={handlePaste}
            />
          ))}
        </Box>

        {/* Panel tóm tắt lỗi cross-check + gợi ý — hiện rõ, không cần hover icon. */}
        {anyError ? (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1,
              border: (t) => `1px solid ${t.palette.error.light}`,
              bgcolor: 'rgba(211,47,47,0.06)',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, color: 'error.main', display: 'block', mb: 0.5 }}>
              Cross-check issues — fix before upload
            </Typography>
            <Stack spacing={0.25}>
              {Object.entries(verdictByRow)
                .filter(([, v]) => !v.ok)
                .sort((a, b) => Number(a[0]) - Number(b[0]))
                .map(([idx, v]) => {
                  // 1 dòng / row: ưu tiên gợi ý max (actionable); nếu chỉ lỗi
                  // required thì gộp message (đã strip prefix "Row N:").
                  const reason = v.suggestion
                    ? `💡 ${v.suggestion}`
                    : v.messages.map((m) => m.replace(/^Row \d+:\s*/, '')).join('; ')
                  return (
                    <Typography
                      key={idx}
                      variant="caption"
                      sx={{ display: 'block', color: 'error.main', fontWeight: 600 }}
                    >
                      {`Row ${Number(idx) + 1}: ${reason}`}
                    </Typography>
                  )
                })}
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Typography
          variant="caption"
          sx={{ mr: 'auto', color: anyError ? 'error.main' : 'text.secondary', fontWeight: anyError ? 700 : 400 }}
        >
          {anyError
            ? `${Object.values(verdictByRow).filter((v) => !v.ok).length} row(s) failed cross-check — fix to enable Upload`
            : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
        </Typography>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={uploading || checking}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void runValidate()}
          disabled={uploading || checking}
        >
          {checking ? <CircularProgress size={16} color="inherit" /> : 'Cross-check'}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => void runUpload()}
          disabled={uploading || checking || anyError}
        >
          {uploading ? <CircularProgress size={16} color="inherit" /> : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const cellSx = {
  '& .MuiInputBase-root': { borderRadius: 0, fontSize: '0.78rem' },
  '& .MuiInputBase-input': { py: 0.5, px: 1 },
  '& fieldset': { border: 'none' },
}

const comboSx = {
  ...cellSx,
  '& .MuiAutocomplete-endAdornment': { right: 2 },
  '& .MuiInputBase-root': { ...cellSx['& .MuiInputBase-root'], pr: '36px !important' },
}

/** Một combo cell restricted: value LUÔN khớp options (không freeSolo → không
 *  commit được giá trị ngoài list). value lệch options → hiển thị rỗng. */
function ComboCell({
  value,
  options,
  loading,
  disabled,
  placeholder,
  onChange,
  onPaste,
}: {
  value: string
  options: string[]
  loading?: boolean
  disabled?: boolean
  placeholder?: string
  onChange: (value: string) => void
  onPaste: (e: React.ClipboardEvent<HTMLElement>) => void
}) {
  // Chỉ feed value cho Autocomplete khi nó nằm trong options (tránh warning
  // "value not in options" + enforce strict list).
  const matched = options.find((o) => o === value) ?? null
  return (
    <Autocomplete
      options={options}
      value={matched}
      onChange={(_, v) => onChange(v ?? '')}
      size="small"
      fullWidth
      disabled={disabled}
      loading={loading}
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      sx={comboSx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          onPasteCapture={onPaste}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

function RowCells({
  row,
  rowIdx,
  factoryCode,
  lotIdOptions,
  stackIdOptions,
  doNoOptions,
  selected,
  verdict,
  onToggle,
  onCell,
  onStack,
  onPaste,
}: {
  row: MassRow
  rowIdx: number
  factoryCode: string
  lotIdOptions: string[]
  stackIdOptions: string[]
  doNoOptions: string[]
  selected: boolean
  verdict?: RowVerdict
  onToggle: () => void
  onCell: (field: FieldKey, value: string) => void
  onStack: (value: string) => void
  onPaste: (rowIdx: number, colIdx: number, e: React.ClipboardEvent<HTMLElement>) => void
}) {
  const lotId = row.lot_id.trim()
  const stackId = row.stack_id.trim()
  const grnId = row.grn_id.trim()
  // grn_id RowSource keyed theo stack của chính dòng này.
  const grnQuery = useGetLotAllocationGrnOptionsQuery(
    { factoryCode, stackId },
    { skip: !stackId },
  )
  const grnOptions = grnQuery.data ?? []

  // GRN keyed theo stack: chưa chọn → AUTO-PICK GRN đầu tiên của stack; đã có
  // giá trị (vd vừa paste) nhưng không thuộc list → clear (lượt sau auto-pick lại,
  // enforce "không cho giá trị ngoài list").
  useEffect(() => {
    if (!stackId || grnQuery.isFetching) return
    const g = row.grn_id.trim()
    if (!g) {
      if (grnOptions.length > 0) onCell('grn_id', grnOptions[0])
      return
    }
    if (!grnOptions.some((o) => o.toLowerCase() === g.toLowerCase())) {
      onCell('grn_id', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnQuery.data, grnQuery.isFetching, row.grn_id, stackId])

  // Pending Bag/Kg snapshot (Access lot_id_AfterUpdate + check_number). Fetch
  // khi lot/stack/grn của dòng đổi.
  const snapshotQuery = useGetLotAllocationSnapshotQuery(
    {
      factoryCode,
      lotId: lotId || null,
      stackId: stackId || null,
      grnId: grnId || null,
      allocationId: null,
    },
    { skip: !lotId && !stackId && !grnId },
  )

  // Auto-fill Bag/Kg = Pending phía GRN/Stack khi key (stack|grn) đổi — mirror
  // single-form check_number. Lot đổi không đụng allocation. Track key để KHÔNG
  // ghi đè giá trị user tự nhập sau đó.
  const prevGrnKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (snapshotQuery.isFetching) return
    const data = snapshotQuery.data
    if (!data) return
    const grnKey = `${stackId}|${grnId}`
    if (grnKey === prevGrnKeyRef.current) return
    prevGrnKeyRef.current = grnKey
    onCell('bag_allocated', formatStandard(data.grn_id_bag))
    onCell('kg_allocated', formatStandard(data.grn_id_kg))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotQuery.data, snapshotQuery.isFetching, stackId, grnId])

  const numberCellSx = {
    borderBottom: (t: { palette: { divider: string } }) => `1px solid ${t.palette.divider}`,
    ...cellSx,
  }

  // Cross-check verdict → tô NỀN ô lỗi: field vi phạm nền đỏ rõ, các ô còn lại
  // của dòng lỗi nền đỏ nhạt (đánh dấu cả dòng).
  const failed = !!verdict && !verdict.ok
  const cellBg = (field?: FieldKey): string | undefined =>
    failed ? (field && verdict!.badFields.has(field) ? 'rgba(211,47,47,0.28)' : 'rgba(211,47,47,0.07)') : undefined

  const renderCombo = (
    colIdx: number,
    field: FieldKey,
    options: string[],
    opts: { loading?: boolean; disabled?: boolean; placeholder?: string; onChange: (v: string) => void },
  ) => (
    <Box
      sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}`, minWidth: 0, bgcolor: cellBg(field) }}
    >
      <ComboCell
        value={row[field]}
        options={options}
        loading={opts.loading}
        disabled={opts.disabled}
        placeholder={opts.placeholder}
        onChange={opts.onChange}
        onPaste={(e) => onPaste(rowIdx, colIdx, e)}
      />
    </Box>
  )

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: selected ? 'action.selected' : cellBg(),
        }}
      >
        <Checkbox size="small" checked={selected} onChange={onToggle} sx={{ p: 0 }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.72rem',
          color: 'text.secondary',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: cellBg(),
        }}
      >
        {rowIdx + 1}
      </Box>
      {/* Cross-check status — ✓ pass / ✗ fail (tooltip liệt kê lý do). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: cellBg(),
        }}
      >
        {verdict ? (
          verdict.ok ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: 'success.main' }} />
          ) : (
            <Tooltip
              title={
                <Box sx={{ whiteSpace: 'pre-line' }}>
                  {verdict.messages.join('\n')}
                  {verdict.suggestion ? (
                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.3)', fontWeight: 700 }}>
                      {`💡 ${verdict.suggestion}`}
                    </Box>
                  ) : null}
                </Box>
              }
            >
              <ErrorRoundedIcon sx={{ fontSize: 16, color: 'error.main' }} />
            </Tooltip>
          )
        ) : null}
      </Box>

      {/* Lot ID — combo restricted */}
      {renderCombo(0, 'lot_id', lotIdOptions, { onChange: (v) => onCell('lot_id', v) })}

      {/* Stack ID — combo restricted (đổi → clear GRN) */}
      {renderCombo(1, 'stack_id', stackIdOptions, { onChange: onStack })}

      {/* GRN ID — combo restricted, keyed theo Stack; optional */}
      {renderCombo(2, 'grn_id', grnOptions, {
        loading: grnQuery.isFetching,
        disabled: !stackId,
        placeholder: stackId ? undefined : 'Stack first',
        onChange: (v) => onCell('grn_id', v),
      })}

      {/* DO No — combo restricted (delivery_order_scs); optional */}
      {renderCombo(3, 'do_no', doNoOptions, { onChange: (v) => onCell('do_no', v) })}

      {/* Bag / Kg Allocated — nhập tay nhưng CHỈ ký tự số (chặn chữ): onChange
          lọc còn [0-9 , .]; blur → format Standard #,##0.00. Auto-fill từ Pending
          GRN vẫn chạy (Stack→auto-pick GRN→snapshot), user gõ đè được. */}
      {FIELDS.slice(COMBO_COUNT).map((f, i) => (
        <TextField
          key={f.key}
          value={row[f.key]}
          onChange={(e) => onCell(f.key, e.target.value.replace(/[^\d.,]/g, ''))}
          onBlur={() => {
            const n = toNum(row[f.key])
            onCell(f.key, n == null ? '' : formatStandard(n))
          }}
          onPasteCapture={(e) => onPaste(rowIdx, COMBO_COUNT + i, e)}
          size="small"
          fullWidth
          variant="outlined"
          placeholder="—"
          inputProps={{ style: { textAlign: 'right' }, inputMode: 'decimal' }}
          sx={{ ...numberCellSx, bgcolor: cellBg(f.key) }}
        />
      ))}
    </>
  )
}

export default LotAllocationMassUploadDialog
